import React, { useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { addCenterPartner } from "../../../store/actions/centerActions/b2bPartnerAction";
import { state_arr, s_a } from "../../misc/StateCitySelector";

/*
  Add a consultant partner under this center (owner, 2026-10-10).

  Only a Consultant Partner can be added here, so there is no type to pick: a
  Business Development Partner registers centers of its own and chooses between
  them, so it does not belong to one center.

  State and city are the council's own two lists, the same pair every other
  form on this site uses - typed in free hand they never agreed with each other.
*/

const BLANK = {
  name: "",
  firm_name: "",
  email: "",
  mobile: "",
  password: "",
  address: "",
  state: "",
  city: "",
  pincode: "",
  experience: "",
  pan_number: "",
  gst_number: "",
};

/* s_a holds each state's towns as one pipe-separated string, offset by one:
   s_a[0] is empty, so Andhra Pradesh at state_arr[1] is s_a[2]. */
const citiesOf = (state) => {
  const i = state_arr.indexOf(state);
  if (i === -1) return [];
  return String(s_a[i + 1] || "")
    .split("|")
    .map((c) => c.trim())
    .filter(Boolean);
};

const AddPartnerModal = ({ show, onClose, onAdded }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => {
    const v = e.target.value;
    /* a city belongs to one state, so it cannot outlive the state it was
       picked under */
    setForm((f) =>
      k === "state" ? { ...f, state: v, city: "" } : { ...f, [k]: v }
    );
  };

  const close = () => {
    setForm(BLANK);
    setErrors({});
    setMessage("");
    onClose();
  };

  const save = async () => {
    setBusy(true);
    setErrors({});
    setMessage("");

    const res = await dispatch(addCenterPartner(form));

    setBusy(false);

    if (res && res.status === "success") {
      setForm(BLANK);
      onAdded(res.message || "Consultant partner added.");
      return;
    }

    /* the server sends the field errors; show them against the fields rather
       than one vague line */
    setErrors((res && res.data) || {});
    setMessage((res && res.message) || "The partner could not be added.");
  };

  const err = (k) => (errors && errors[k] ? errors[k][0] : "");

  const field = (k, label, opts = {}) => (
    <div className="col-md-6 mb-3">
      <Form.Label>
        {label}
        {opts.required ? <span className="text-danger"> *</span> : null}
      </Form.Label>
      <Form.Control
        type={opts.type || "text"}
        value={form[k]}
        onChange={set(k)}
        isInvalid={!!err(k)}
        autoComplete="off"
      />
      {err(k) ? (
        <div className="text-danger small mt-1">{err(k)}</div>
      ) : opts.hint ? (
        <div className="text-muted small mt-1">{opts.hint}</div>
      ) : null}
    </div>
  );

  return (
    <Modal show={show} onHide={close} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Add Consultant Partner</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-muted">
          The students this consultant registers will come under this center.
          The council approves the partner before it can sign in, and the
          council sets its commission.
        </p>

        {message ? (
          <div className="alert alert-danger">{message}</div>
        ) : null}

        <div className="row">
          {field("name", "Contact person", { required: true })}
          {field("firm_name", "Organization")}
          {field("email", "Email", { required: true, type: "email" })}
          {field("mobile", "Mobile", { required: true })}
          {field("password", "Password", {
            required: true,
            type: "password",
            hint: "At least 8 characters. The partner signs in with this and can change it later.",
          })}
          {field("experience", "Experience")}

          <div className="col-md-12 mb-3">
            <Form.Label>Address</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.address}
              onChange={set("address")}
            />
          </div>

          <div className="col-md-4 mb-3">
            <Form.Label>State</Form.Label>
            <Form.Control as="select" value={form.state} onChange={set("state")}>
              <option value="">Select state</option>
              {state_arr.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Form.Control>
          </div>

          <div className="col-md-4 mb-3">
            <Form.Label>City</Form.Label>
            <Form.Control
              as="select"
              value={form.city}
              onChange={set("city")}
              disabled={!form.state}
            >
              <option value="">
                {form.state ? "Select city" : "Choose a state first"}
              </option>
              {citiesOf(form.state).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Form.Control>
          </div>

          <div className="col-md-4 mb-3">
            <Form.Label>Pincode</Form.Label>
            <Form.Control value={form.pincode} onChange={set("pincode")} />
          </div>

          {field("pan_number", "PAN")}
          {field("gst_number", "GST")}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={close} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" onClick={save} disabled={busy}>
          {busy ? (
            <>
              <Spinner animation="border" size="sm" /> Saving
            </>
          ) : (
            "Add partner"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddPartnerModal;
